import { useEffect, useRef, useState } from "react";
import { Renderer, Program, Mesh, Color, Triangle } from "ogl";

import styles from "./Aurora.module.css";

const VERT = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAG = `#version 300 es
precision highp float;

uniform float uTime;
uniform float uAmplitude;
uniform vec3 uColorStops[3];
uniform vec2 uResolution;
uniform float uBlend;
uniform float uNoiseIntensity;
uniform float uNoiseScale;

out vec4 fragColor;

// Permutation function for noise
vec3 permute(vec3 x) {
  return mod(((x * 34.0) + 1.0) * x, 289.0);
}

// Simplex noise function
float snoise(vec2 v){
  const vec4 C = vec4(
      0.211324865405187, 0.366025403784439,
      -0.577350269189626, 0.024390243902439
  );
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);

  vec3 p = permute(
      permute(i.y + vec3(0.0, i1.y, 1.0))
    + i.x + vec3(0.0, i1.x, 1.0)
  );

  vec3 m = max(
      0.5 - vec3(
          dot(x0, x0),
          dot(x12.xy, x12.xy),
          dot(x12.zw, x12.zw)
      ), 
      0.0
  );
  m = m * m;
  m = m * m;

  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);

  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// Fast random function for grain effect
float random(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}

// Ultra-sharp high-quality film grain function
float filmGrain(vec2 uv, float time) {
  // High-resolution sharp grain particles
  float grain = 0.0;
  
  // Ultra-fine grain layer (crisp detail)
  vec2 uvRandom = uv * 4.0;
  uvRandom.y *= random(vec2(uvRandom.y, time));
  grain += random(uvRandom) * 0.7;
  
  // Super-fine secondary layer for texture depth
  uvRandom = uv * 5.5;
  uvRandom.x *= random(vec2(uvRandom.x, time * 0.3));
  grain += random(uvRandom) * 0.3;
  
  return grain;
}

struct ColorStop {
  vec3 color;
  float position;
};

#define COLOR_RAMP(colors, factor, finalColor) {              \\
  int index = 0;                                            \\
  for (int i = 0; i < 2; i++) {                               \\
     ColorStop currentColor = colors[i];                    \\
     bool isInBetween = currentColor.position <= factor;    \\
     index = int(mix(float(index), float(i), float(isInBetween))); \\
  }                                                         \\
  ColorStop currentColor = colors[index];                   \\
  ColorStop nextColor = colors[index + 1];                  \\
  float range = nextColor.position - currentColor.position; \\
  float lerpFactor = (factor - currentColor.position) / range; \\
  finalColor = mix(currentColor.color, nextColor.color, lerpFactor); \\
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  
  // Create color stops for gradient
  ColorStop colors[3];
  colors[0] = ColorStop(uColorStops[0], 0.0);
  colors[1] = ColorStop(uColorStops[1], 0.5);
  colors[2] = ColorStop(uColorStops[2], 1.0);
  
  vec3 rampColor;
  COLOR_RAMP(colors, uv.x, rampColor);
  
  // Create the flowing aurora effect
  float height = snoise(vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)) * 0.5 * uAmplitude;
  height = exp(height);
  height = (uv.y * 2.0 - height + 0.2);
  float intensity = 0.6 * height;
  
  float midPoint = 0.20;
  float auroraAlpha = smoothstep(midPoint - uBlend * 0.5, midPoint + uBlend * 0.5, intensity);
  
  vec3 auroraColor = intensity * rampColor;
  
  // Ultra-sharp, high-quality grain effect
  vec2 grainUV = uv * uNoiseScale;
  
  // Primary ultra-sharp grain layer
  float grain1 = filmGrain(grainUV, uTime * 0.5);
  
  // Minimal simplex noise for subtle organic texture (keep it sharp)
  float grain2 = snoise(grainUV * 2.0 + uTime * 0.01) * 0.5 + 0.5;
  
  // Combine with minimal blending to preserve sharpness
  float finalGrain = mix(grain1, grain2, 0.1);
  
  // Apply grain with crisp intensity
  float grainIntensity = uNoiseIntensity * (1.0 + auroraAlpha * 0.15);
  
  // Ultra-sharp grain application with minimal blur
  vec3 grainColor = vec3(finalGrain) * grainIntensity;
  auroraColor = auroraColor + (grainColor - 0.5 * grainIntensity) * 1.4;
  
  // High-frequency color noise for crisp detail
  vec3 colorNoise = vec3(
    random(uv * 4.0 + uTime * 0.005),
    random(uv * 4.5 + uTime * 0.007),
    random(uv * 5.0 + uTime * 0.006)
  );
  auroraColor += (colorNoise - 0.5) * uNoiseIntensity * 0.08;
  
  fragColor = vec4(auroraColor * auroraAlpha, auroraAlpha);
}
`;

export default function AuroraGrain(props) {
  const {
    colorStops = ["#00A0B0", "#9C0076", "#FF4500"],
    amplitude = 1.0,
    blend = 0.5,
    speed = 1.0,
    noiseIntensity = 0.05,
    noiseScale = 200.0
  } = props;

  const [mounted, setMounted] = useState(false);
  const propsRef = useRef(props);
  propsRef.current = props;

  const ctnDom = useRef(null);

  // Set mounted state after client-side hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Skip SSR - only run on client
    if (typeof window === 'undefined' || !mounted) return;

    const ctn = ctnDom.current;
    if (!ctn) return;

    const renderer = new Renderer({
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
    });
    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.canvas.style.backgroundColor = "transparent";

    let program;

    function resize() {
      if (!ctn) return;
      const width = ctn.offsetWidth;
      const height = ctn.offsetHeight;
      renderer.setSize(width, height);
      if (program) {
        program.uniforms.uResolution.value = [width, height];
      }
    }
    window.addEventListener("resize", resize);

    const geometry = new Triangle(gl);
    if (geometry.attributes.uv) {
      delete geometry.attributes.uv;
    }

    const colorStopsArray = colorStops.map((hex) => {
      const c = new Color(hex);
      return [c.r, c.g, c.b];
    });

    program = new Program(gl, {
      vertex: VERT,
      fragment: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uAmplitude: { value: amplitude },
        uColorStops: { value: colorStopsArray },
        uResolution: { value: [ctn.offsetWidth, ctn.offsetHeight] },
        uBlend: { value: blend },
        uNoiseIntensity: { value: noiseIntensity },
        uNoiseScale: { value: noiseScale }
      },
    });

    const mesh = new Mesh(gl, { geometry, program });
    ctn.appendChild(gl.canvas);

    let animateId = 0;
    const update = (t) => {
      animateId = requestAnimationFrame(update);
      const {
        time = t * 0.01,
        speed: currentSpeed = speed,
        noiseIntensity: currentNoiseIntensity = noiseIntensity,
        noiseScale: currentNoiseScale = noiseScale
      } = propsRef.current;

      if (program) {
        program.uniforms.uTime.value = time * currentSpeed * 0.1;
        program.uniforms.uAmplitude.value = propsRef.current.amplitude ?? amplitude;
        program.uniforms.uBlend.value = propsRef.current.blend ?? blend;
        program.uniforms.uNoiseIntensity.value = currentNoiseIntensity;
        program.uniforms.uNoiseScale.value = currentNoiseScale;

        const stops = propsRef.current.colorStops ?? colorStops;
        program.uniforms.uColorStops.value = stops.map((hex) => {
          const c = new Color(hex);
          return [c.r, c.g, c.b];
        });
        renderer.render({ scene: mesh });
      }
    };
    animateId = requestAnimationFrame(update);

    resize();

    return () => {
      cancelAnimationFrame(animateId);
      window.removeEventListener("resize", resize);
      if (ctn && gl.canvas.parentNode === ctn) {
        ctn.removeChild(gl.canvas);
      }
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [amplitude, blend, colorStops, noiseIntensity, noiseScale, speed, mounted]);

  return (
    <div
      ref={ctnDom}
      className={styles['aurora-container']}
      style={{
        opacity: mounted ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out'
      }}
    />
  );
} 